import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/ord-orders/my-stores-orders/{order_uuid}/{store_uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetMyStoresOrderDetailInput = paths[url]['get']['parameters']['path'];
export type GetMyStoresOrderDetailSuccessResponse = paths[url]['get']['responses']['200']['content']['application/json'];

/**
 * Requests endpoint to get a user's stores' order.
 */
export const useGetMyStoresOrderDetail = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setMyStoresOrderDetail, setIsLoadingMyStoresOrderDetail } = useData();

    const request = async (props: GetMyStoresOrderDetailInput) => {
        setIsLoadingMyStoresOrderDetail(true);

        try {
            const inputParameters = new URLSearchParams(getCommonParameters()).toString();

            const response = await fetch(`${API_URL}${ENDPOINT_URL.replace('{order_uuid}', props.order_uuid || '').replace('{store_uuid}', props.store_uuid || '')}?${inputParameters}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch (error: any) {
                    error.message = '';
                }
                
                error.statusCode = response.status;
                
                throw error;
            }

            const data: GetMyStoresOrderDetailSuccessResponse = await response.json();

            setMyStoresOrderDetail(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
        } finally {
            setIsLoadingMyStoresOrderDetail(false);
        }
    };

    return { request };
};
